/* eslint-disable react/prop-types */
import React, {
  useEffect, useState, useRef,
} from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table, Input, Modal, Button, Col,
} from 'antd';

import { PlusOutlined } from '@ant-design/icons';

import InfoView from '../../components/common/InfoView';
import 'jspdf-autotable';
import DownloadBtns from '../../components/common/downloadbtns';
import {
  fetchMerchantBeneficiaries,
  createMerchantBeneficiary,
  searchMerchantAccounts,
  deleteMerchantBeneficiary,
  updateMerchantBeneficiary,
  fetchMerchantBeneficiariesById,
} from '../../redux/actions/Data';
import MerchantBeneficiaryForm from './MerchantBeneficiaryForm';
import {
  handlePdf, handleXcl, generatePdfData, generateXclData,
} from '../../util/dwnload';
import Can from '../../util/can';
import { MERCHANT_BENEFICIARIES } from '../../constants/resources';
import { merchantBeneficiaryColumns } from '../../components/common/Columns';
import usePrevious from '../../hooks/usePrevious';

const MerchantBeneficiary = ({ merchantId }) => {
  const dispatch = useDispatch();
  const { timezone } = useSelector(({ settings }) => settings);
  const {
    creatingMerchantBeneficiary,
    updatingMerchantBeneficiary,
    data: merchantBeneficiaries,
    loading,
    currentPage: current,
    pageSize,
    totalItems,
  } = useSelector(({ data }) => data.merchantBeneficiaries);
  const merchants = useSelector(({ data }) => data.searchMerchants);

  const prevChargeCreating = usePrevious(creatingMerchantBeneficiary);
  const prevChargeUpdating = usePrevious(updatingMerchantBeneficiary);

  const [showModal, setShowModal] = useState(false);
  const [showModalTesting, setShowModalTesting] = useState(false);
  const [editableId, setEditableId] = useState(false);
  const [selectedMerchantBeneficiary, setSelectedMerchantBeneficiary] = useState(null);
  const [selectedMinimumAmount, setSelectedMinimumAmount] = useState(0);
  const [selectedMaximumAmount, setSelectedMaximumAmount] = useState(0);
  const [selectedProviders, setSelectedProviders] = useState('');
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState('');

  const form = useRef();

  const selectedMerchant = useSelector(({ data }) => data.selectedMerchantAccount);

  useEffect(() => {
    const pagination = `page=${current}&limit=${pageSize}`;
    if (prevChargeCreating === true
      || prevChargeUpdating === true
      || merchantBeneficiaries.length === 0) {
      if (merchantId) {
        dispatch(fetchMerchantBeneficiariesById(merchantId, pagination));
      } else {
        dispatch(fetchMerchantBeneficiaries(pagination));
      }
    }
  }, [dispatch, prevChargeCreating, prevChargeUpdating]);

  useEffect(() => {
    if (!merchants.length) {
      dispatch(searchMerchantAccounts());
    }
  }, [dispatch]);

  const handleDeleteMerchantBeneficiary = (id) => {
    dispatch(deleteMerchantBeneficiary(id));
  };

  const handleTesting = (record) => {
    setSelectedMerchantBeneficiary(record);
    setShowModalTesting(true);
  };

  const handleTestingCancel = () => {
    setSelectedMerchantBeneficiary(null);
    setShowModalTesting(false);
  };

  const handleEditMerchantBeneficiary = (record) => {
    setEditableId(record._id);
    setSelectedProviders(record.provider);
    setSelectedTransactionTypes(record.transaction_type);
    setSelectedMinimumAmount(
      record.minimum_amount,
    );
    setSelectedMaximumAmount(
      record.maximum_amount,
    );
    setShowModal(true);
    setTimeout(() => {
      const values = {
        ...record,
        fixed_charge: record.fixed_charge,
        percentage_charge: record.percentage_charge,
        refund_charge: record.refund_charge,
        settlement_charge: record.settlement_charge,
        chargeback_charge: record.chargeback_charge,
        merchant: record.merchant._id,
        provider: record.provider,
        minimum_amount: record.minimum_amount,
        maximum_amount: record.maximum_amount,
      };
      form.current.setFieldsValue(values);
    }, 300);
  };

  const handleOnChange = (pagination, filters, sorter) => {
    let query = '';
    if (pagination && Object.keys(pagination).length) {
      query += `&page=${pagination.current}&limit=${pagination.pageSize}`;
    }
    if (filters && Object.keys(filters).length) {
      const fields = Object.keys(filters).filter((key) => filters[key]);
      if (fields.length) {
        const filterFilters = fields.map((field) => ({ [field.split('.')[0]]: `${filters[field][0]}` }));
        const filterParams = JSON.stringify(filterFilters);
        query += `&filter={"$or": ${filterParams}}`;
      }
    }
    if (sorter && Object.keys(sorter).length && sorter.field) {
      query += `&sort={"${sorter.field}": ${sorter.order === 'ascend' ? '1' : '-1'} }`;
    }

    if (merchantId) {
      dispatch(fetchMerchantBeneficiariesById(merchantId, query));
    } else {
      dispatch(fetchMerchantBeneficiaries(query));
    }
  };

  const handleSearch = (searchValue) => {
    const _searchValue = searchValue.trim().replace(/([^a-zA-Z0-9])/g, '\\$1');
    if (_searchValue) {
      const searchFields = ['account_name'];
      let searchParams = JSON.stringify(
        searchFields.map((field) => ({
          [field]: { $regex: _searchValue.trim(), $options: 'i' },
        })),
      );
      if (!Number.isNaN(Number(_searchValue))) {
        searchParams = JSON.stringify([{ fixed_charge: Number(_searchValue) }, {
          percentage_charge: Number(_searchValue),
        }]);
      }
      const search = `filter={"$or": ${searchParams}}`;
      if (merchantId) {
        dispatch(fetchMerchantBeneficiariesById(merchantId, search));
      } else {
        dispatch(fetchMerchantBeneficiaries(search));
      }
    } else {
      const pagination = `page=${current}&limit=${pageSize}`;
      if (merchantId) {
        dispatch(fetchMerchantBeneficiariesById(merchantId, pagination));
      } else {
        dispatch(fetchMerchantBeneficiaries(pagination));
      }
    }
  };

  const props = {
    loading: loading,
    pagination: {
      current,
      pageSize,
      total: totalItems,
      showSizeChanger: false,
    },
    scroll: 'y',
    rowKey: '_id',
    columns: merchantBeneficiaryColumns(
      handleDeleteMerchantBeneficiary,
      handleEditMerchantBeneficiary,
      merchants,
      selectedMerchant,
      handleTesting
    ),
    dataSource: merchantBeneficiaries,
    onChange: handleOnChange,
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditableId('');
    setSelectedProviders(null);
    setSelectedTransactionTypes(null);
  };

  const handleOk = () => {
    if (form.current) {
      form.current.validateFields().then((values) => {
        if (editableId) {
          dispatch(updateMerchantBeneficiary(editableId, {
            merchant: selectedMerchant._id,
            ...values,
            minimum_amount: selectedMinimumAmount,
            maximum_amount: selectedMaximumAmount,
            provider: selectedProviders,
            transaction_type: selectedTransactionTypes,
          }));
        } else {
          dispatch(createMerchantBeneficiary({
            merchant: selectedMerchant._id,
            ...values,
            minimum_amount: selectedMinimumAmount,
            maximum_amount: selectedMaximumAmount,
            provider: selectedProviders,
            transaction_type: selectedTransactionTypes,
          }));
        }

        setTimeout(() => {
          setShowModal(false);
          setEditableId('');
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  const handleShowModal = () => {
    if (form.current) {
      if (merchantId) {
        const values = {
          merchant: merchantId,
        };
        form.current.resetFields();
        form.current.setFieldsValue(values);
      } else {
        form.current.resetFields();
      }
    }
    setShowModal(true);
  };

  const handleChange = (value, key) => {
    if (key === 'minimum_amount') {
      setSelectedMinimumAmount(value);
    } else if (key === 'maximum_amount') {
      setSelectedMaximumAmount(value);
    }
  };

  const handleChangeProviders = (value) => {
    setSelectedProviders(value);
  };

  const handleChangeTransactionTypes = (value) => {
    setSelectedTransactionTypes(value);
  };

  function handleDataItemArray(row, item, dataKey) {
    const newRow = row;

    return newRow;
  }

  const renderMerchantBenefiaciaries = () => (
    <>
      <div className="gx-d-flex justify-content-center">
        {selectedMerchant && (
          <Can I="create" a={MERCHANT_BENEFICIARIES}>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => handleShowModal()}
            >
              Add Merchant Charge
            </Button>

          </Can>
        )}
        <Input.Search
          placeholder="Search merchant charges"
          enterButton
          allowClear
          onSearch={handleSearch}
        />
        <Col xl={4} lg={6} md={7} sm={7} xs={6}>
          <DownloadBtns
            handlePdf={() => handlePdf(props.columns,
              generatePdfData(props.dataSource, props.columns, handleDataItemArray),
              'Merchant Charges', timezone)}
            handleXcl={() => handleXcl(props.columns,
              generateXclData(props.dataSource, props.columns, handleDataItemArray),
              'Merchant Charges', timezone)}
          />
        </Col>
      </div>
      <div className="gx-d-flex justify-content-center">
        <Table className="gx-table-responsive" {...props} />
      </div>
      <Modal
        title={editableId ? 'Edit Merchant Charge' : 'Add Merchant Charge'}
        open={showModal}
        okText={editableId ? 'Save' : 'Add'}
        onOk={handleOk}
        confirmLoading={(creatingMerchantBeneficiary || updatingMerchantBeneficiary)}
        onCancel={handleCancel}
        centered
      >
        <MerchantBeneficiaryForm {...{
          form,
          handleChange,
          minimumAmount: Number(selectedMinimumAmount),
          maximumAmount: Number(selectedMaximumAmount),
          handleChangeProviders,
          selectedProviders,
          handleChangeTransactionTypes,
          selectedTransactionTypes
        }}
        />
      </Modal>
      <InfoView />
    </>
  );

  return merchantId
    ? (
      <>
        {renderMerchantBenefiaciaries()}
      </>
    )
    : (
      <div>
        <h2 className="title gx-mb-4">Merchant Charges</h2>
        {renderMerchantBenefiaciaries()}
      </div>
    );
};

MerchantBeneficiary.propTypes = {
  merchantId: PropTypes.string,
};

MerchantBeneficiary.defaultProps = {
  merchantId: '',
};

export default MerchantBeneficiary;
